import { useState, useEffect, useCallback } from 'react';
import { Plus, X, ChevronDown, Check, Copy, Loader2, ArrowRight, Delete } from 'lucide-react';
import apiV2 from '../utils/apiV2';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { hapticLight } from '../utils/haptics';

const PHONE_PREFIXES = ['+971', '+1', '+44', '+33', '+49', '+34', '+39', '+61', '+91', '+966', '+974', '+965', '+968', '+973'];
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NUMPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'del'];

const inputCls = "w-full h-11 px-3.5 text-[14px] rounded-xl bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all";
const labelCls = "block text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5";

/** Quick Invoice — floating ＋ in the bottom-right corner. One step:
 *  amount → charge. Everything else lives under Advanced options. */
export const QuickInvoiceLauncher = ({ orgId }) => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!orgId || config) return;
    apiV2.get(`/organizations/${orgId}/invoices/config`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ provider: 'stripe', currency: 'USD', currencies: ['USD', 'AED', 'EUR', 'GBP'], taxLabel: 'Tax', taxDefaultRate: 0, phonePrefix: '+1', fxFeeRate: 0.03 }));
  }, [orgId, config]);

  if (!orgId) return null;
  return (
    <>
      <button
        data-testid="quick-invoice-fab"
        onClick={() => setOpen(true)}
        aria-label="New charge"
        className="fixed right-5 bottom-[calc(env(safe-area-inset-bottom,0px)+84px)] lg:bottom-5 z-[59] flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-[0_8px_24px_rgba(37,99,235,0.4)] transition-transform hover:scale-105 hover:bg-blue-700 focus:outline-none"
      >
        <Plus className="h-6 w-6" strokeWidth={2.5} />
      </button>
      {open && <QuickInvoiceSheet orgId={orgId} config={config} onClose={() => setOpen(false)} />}
    </>
  );
};

const QuickInvoiceSheet = ({ orgId, config, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [prefix, setPrefix] = useState('');
  const [phone, setPhone] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [bookingCode, setBookingCode] = useState('');
  const [taxOn, setTaxOn] = useState(false);
  const [taxRate, setTaxRate] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!config) return;
    if (!currency) setCurrency(config.currency);
    if (!prefix) setPrefix(config.phonePrefix || '+1');
    if (taxRate === '') setTaxRate(String(config.taxDefaultRate ?? 0));
  }, [config]); // eslint-disable-line

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const taxLabel = config?.taxLabel || 'Tax';
  const curr = currency || config?.currency || 'USD';
  const amt = parseFloat(amount) || 0;
  const rate = taxOn ? (parseFloat(taxRate) || 0) : 0;
  const total = Math.round((amt + (amt * rate) / 100) * 100) / 100;
  const isFx = config?.provider === 'stripe' && currency && currency !== config?.currency;
  const isUae = (config?.country || '').toLowerCase().includes('uae') || config?.phonePrefix === '+971';

  const create = useCallback(async () => {
    if (!(amt > 0) || busy) return;
    setBusy(true);
    try {
      const fullPhone = phone.trim() ? `${prefix}${phone.trim().replace(/^0+/, '').replace(/[^\d]/g, '')}` : null;
      const r = await apiV2.post(`/organizations/${orgId}/invoices`, {
        amount: amt,
        currency: curr,
        customerName: name.trim() || null,
        customerEmail: email.trim() || null,
        customerPhone: fullPhone,
        description: description.trim() || null,
        bookingCode: bookingCode.trim() || null,
        vatRate: rate || null,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the charge');
    }
    setBusy(false);
  }, [amt, busy, orgId, curr, name, email, phone, prefix, description, bookingCode, rate]);

  const reset = () => {
    setResult(null); setAmount(''); setName(''); setDescription('');
    setEmail(''); setPhone(''); setBookingCode(''); setTaxOn(false); setAdvanced(false);
  };

  // POS numpad (design system P2) — thumb entry on mobile, clickable on desktop
  const isTouch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  const press = (k) => {
    hapticLight();
    setAmount((a) => {
      if (k === 'del') return a.slice(0, -1);
      if (k === '.') return a.includes('.') ? a : (a === '' ? '0.' : a + '.');
      const dec = a.split('.')[1];
      if (dec && dec.length >= 2) return a;
      if (a.replace('.', '').length >= 7) return a;
      return a === '0' ? k : a + k;
    });
  };

  return (
    <div className="fixed inset-0 z-[80] sm:flex sm:items-center sm:justify-center" data-testid="quick-invoice-sheet">
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-[2px] animate-in fade-in duration-200" onClick={onClose} />
      <div className="absolute bottom-0 inset-x-0 sm:relative sm:inset-auto sm:w-[420px] bg-white rounded-t-3xl sm:rounded-3xl shadow-[0_18px_50px_rgba(2,12,27,0.25)] animate-in slide-in-from-bottom sm:zoom-in-95 duration-300 max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white rounded-t-3xl px-6 pt-5 pb-1 flex items-center gap-2">
          <span className="w-9 h-1 rounded-full bg-gray-200 absolute left-1/2 -translate-x-1/2 top-2 sm:hidden" />
          <p className="text-[15px] font-semibold text-gray-900 flex-1">{result ? 'Charge ready' : 'New charge'}</p>
          <button data-testid="qi-close-btn" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 bg-gray-50 hover:text-gray-700 hover:bg-gray-100 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Charge form · P2 POS numpad · amount → pay ── */}
        {!result && (
          <div className="px-6 pb-5 space-y-4">
            <div className="text-center">
              <div className="relative inline-block">
                <select
                  data-testid="qi-currency-select"
                  value={currency || config?.currency || 'USD'}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="appearance-none h-7 pl-3 pr-7 text-[12px] font-bold rounded-full bg-gray-100 text-gray-600 focus:outline-none cursor-pointer"
                >
                  {(config?.currencies || ['USD']).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
              <input
                data-testid="qi-amount-input"
                autoFocus={!isTouch}
                readOnly={isTouch}
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                className="mt-1 w-full bg-transparent text-center text-[40px] font-extrabold leading-none tracking-tight tabular-nums text-gray-900 placeholder:text-gray-200 focus:outline-none"
              />
              {isFx && (
                <p className="text-[12px] text-gray-400 mt-1" data-testid="qi-fx-note">
                  Non-{config.currency} charge — a {Math.round((config.fxFeeRate || 0.03) * 100)}% currency conversion fee applies.
                </p>
              )}
            </div>

            {/* Numpad — gray-50 keys, rounded-2xl (P2) */}
            <div className="grid grid-cols-3 gap-1" data-testid="qi-numpad">
              {NUMPAD_KEYS.map((k) => (
                <button
                  key={k}
                  data-testid={`qi-key-${k === '.' ? 'dot' : k}`}
                  onClick={() => press(k)}
                  className="flex items-center justify-center rounded-2xl py-2.5 text-lg font-semibold tabular-nums text-gray-900 transition-colors hover:bg-gray-50 active:bg-gray-100"
                >
                  {k === 'del' ? <Delete className="h-5 w-5 text-gray-500" /> : k}
                </button>
              ))}
            </div>

            {/* Advanced options — everything optional lives here */}
            <div className="pt-1">
              <button
                data-testid="qi-advanced-toggle"
                onClick={() => setAdvanced(!advanced)}
                className="flex w-full items-center justify-between text-[13px] font-medium text-gray-500 hover:text-gray-800 transition-colors"
              >
                Advanced options
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${advanced ? 'rotate-180' : ''}`} />
              </button>
              {advanced && (
                <div className="space-y-3 mt-4">
                  <div>
                    <label className={labelCls}>Name <span className="normal-case font-normal text-gray-300">— optional</span></label>
                    <input data-testid="qi-name-input" placeholder="Customer name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Description <span className="normal-case font-normal text-gray-300">— optional, shows on the invoice</span></label>
                    <input data-testid="qi-description-input" placeholder="e.g. Crew tip, fuel surcharge, extra hour" value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Email the invoice to <span className="normal-case font-normal text-gray-300">— optional</span></label>
                    <input data-testid="qi-email-input" type="email" placeholder="customer@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>
                      {isUae ? 'WhatsApp it to' : 'Text it to'} <span className="normal-case font-normal text-gray-300">— optional</span>
                    </label>
                    <div className="flex gap-2">
                      <div className="relative shrink-0">
                        <select
                          data-testid="qi-phone-prefix-select"
                          value={prefix}
                          onChange={(e) => setPrefix(e.target.value)}
                          className="appearance-none h-11 pl-3 pr-7 text-[14px] rounded-xl bg-gray-50 text-gray-700 focus:outline-none cursor-pointer tabular-nums"
                        >
                          {[...new Set([config?.phonePrefix || '+1', ...PHONE_PREFIXES])].map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                      <input data-testid="qi-phone-input" type="tel" inputMode="tel" placeholder="50 123 4567" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Link to a booking</label>
                    <input data-testid="qi-booking-code-input" placeholder="Confirmation code, e.g. BK-1234ABCD" value={bookingCode} onChange={(e) => setBookingCode(e.target.value.toUpperCase())} className={inputCls} />
                  </div>
                  <div className="flex items-center justify-between h-11 px-3.5 bg-gray-50 rounded-xl">
                    <span className="text-[14px] text-gray-700">Add {taxLabel} on top</span>
                    <span className="flex items-center gap-2.5">
                      {taxOn && (
                        <span className="flex items-center gap-1 text-[14px] text-gray-700 tabular-nums">
                          <input data-testid="qi-vat-rate-input" inputMode="decimal" value={taxRate} onChange={(e) => setTaxRate(e.target.value.replace(/[^\d.]/g, ''))} className="w-12 h-7 px-1 text-center text-[13px] bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100" />%
                        </span>
                      )}
                      <Switch data-testid="qi-vat-toggle" checked={taxOn} onCheckedChange={setTaxOn} />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Charge summary — only when tax changes the total */}
            {rate > 0 && (
              <div className="rounded-xl bg-gray-50 px-4 py-3 space-y-1" data-testid="qi-total-preview">
                <div className="flex justify-between text-[13px] text-gray-500">
                  <span>{description || 'Charge'}</span>
                  <span className="tabular-nums">{curr} {fmt(amt)}</span>
                </div>
                <div className="flex justify-between text-[13px] text-gray-500">
                  <span>{taxLabel} ({rate}%)</span>
                  <span className="tabular-nums">{curr} {fmt(total - amt)}</span>
                </div>
                <div className="flex justify-between text-[14px] font-semibold text-gray-900 pt-1 border-t border-gray-200/70">
                  <span>Total</span>
                  <span className="tabular-nums">{curr} {fmt(total)}</span>
                </div>
              </div>
            )}

            <button
              data-testid="qi-create-btn"
              onClick={create}
              disabled={!(amt > 0) || busy}
              className="w-full py-3.5 rounded-full bg-blue-600 text-white text-[15px] font-semibold disabled:opacity-30 hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {busy ? 'Creating…' : `Charge ${curr} ${fmt(total)}`}
            </button>
          </div>
        )}

        {/* ── Done · charge ready ── */}
        {result && (
          <div className="px-6 py-6 space-y-5 text-center">
            <div className="mx-auto w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center">
              <Check className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-semibold tracking-tight text-gray-900 tabular-nums" data-testid="qi-done-total">
                {result.currency} {fmt(result.total)}
              </p>
              <p className="text-[12px] text-gray-400 mt-1" data-testid="qi-invoice-number">
                {result.invoiceNumber} · via {result.provider === 'ziina' ? 'Ziina' : 'Stripe'}
              </p>
              {result.sentTo?.length > 0 && (
                <p className="text-[13px] text-gray-500 mt-2 flex items-center justify-center gap-1.5" data-testid="qi-sent-to">
                  <Check className="w-3.5 h-3.5 text-emerald-500" /> Invoice sent to {result.sentTo.join(' & ')}
                </p>
              )}
            </div>
            <button
              data-testid="qi-goto-pay-btn"
              onClick={() => window.location.assign(result.checkoutUrl)}
              className="w-full py-3.5 rounded-full bg-blue-600 text-white text-[15px] font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              Go to pay <ArrowRight className="w-4 h-4" />
            </button>
            <div className="flex items-center justify-center gap-6">
              <button
                data-testid="qi-copy-link-btn"
                onClick={() => { navigator.clipboard.writeText(result.checkoutUrl); toast.success('Payment link copied'); }}
                className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-900 transition-colors"
              >
                <Copy className="w-3.5 h-3.5" /> Copy link
              </button>
              <button data-testid="qi-new-charge-btn" onClick={reset} className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-900 transition-colors">
                <Plus className="w-3.5 h-3.5" /> New charge
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuickInvoiceLauncher;
